import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Table, Pagination, ConfigProvider, Button } from 'antd';
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import moment from 'moment';
import { DataContext } from '../../init/getData';
import toast from 'react-hot-toast';
import { numberFormatter } from '../../components/formatters/numberFormatter';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { BiExport } from "react-icons/bi";
import exportToExcel from '../export/excel';
import ReportModal from '../../components/modals/reportModal';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

// DND Kit imports
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';

// Row Context for DND Kit
const RowContext = React.createContext({});

// Drag Handle Component
const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'grab' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

// Custom Row Component for DND Kit
const DraggableRow = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const DragableTable = (props) => {
  const {
    filter,
    table,
    refreshTable,
    columnDefs,
    data,
    setData,
    tableFooter,
    height,
    getAllData,
    rowColor,
    setCheckedArray,  
    selection,
    api,
    getExcelData,
    title,
    report,
    reportRef,
    onRowClick,
    statementreport,
    statementreportRef,
    getExcelScale,
    isDraggable, 
    onDragEnd: onDragEndProp
  } = props;

  const { userData, settingData, refCompany } = useContext(DataContext);

  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState({ pagination: { current: 1, pageSize: 100, }, });

  const [showReportModal, setShowReportModal] = useState(false);
  const [showStatementReportModal, setShowStatementReportModal] = useState(false);

  // Internal state for dataSource, synced with external 'data' prop
  const [dataSource, setDataSource] = useState(data && data.data ? data.data : []); // Ensure initial state is an array

  useEffect(() => {
    // Ensure dataSource is always an array when 'data' prop changes
    setDataSource(data && data.data ? data.data : []);
  }, [data]);

  const fetchData = async () => {
    setLoading(true);

    const sortField = tableParams.field;
    const sortDirection = tableParams.order;

    let requestData = {
      filter: filter,
      startRow: getAllData ? null : (tableParams.pagination.current - 1) * tableParams.pagination.pageSize,
      endRow: getAllData ? null : (tableParams.pagination.current) * tableParams.pagination.pageSize,
      sortField: sortField,
      sortDirection: sortDirection,
    };

    if (filter.search) {
      requestData.startRow = null;
      requestData.endRow = null;
    }

    if (tableParams.column) {
      requestData.sortField = tableParams.field;
      requestData.sortDirection = tableParams.order === "ascend" ? 'asc' : 'desc';
    }

    try {
      const response = await api.get(`/${table}/getall`, { params: requestData });
      if (!response.data.status) {
        toast.error('Failed to get Data');
      } else {
        setData(response.data); // Update external data prop
        // Ensure response.data.data is an array before setting dataSource
        setDataSource(response.data.data || []); // FIX: Added || []
        setLoading(false);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: response.data.totalRecords,
            showSizeChanger: false,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error); // Log the error for debugging
      toast.error('Error fetching data');
      setLoading(false);
    }
  };

  const exportDataToExcel = async () => {
    setLoading(true);
    let requestData = {
      filter: filter,
      startRow: null,
      endRow: null,
    };

    if (tableParams.column) {
      requestData.sortField = tableParams.field;
      requestData.sortDirection = tableParams.order === "ascend" ? 'asc' : 'desc';
    }

    try {
      const response = await api.get(`/${table}/getall`, { params: requestData });
      if (!response.data.status) {
        toast.error('Failed to get Data');
      } else {
        setLoading(false);
        const excelData = getExcelData(response.data.data || [], response.data.summary); // Ensure data is array
        exportToExcel(excelData, title);
      }
    } catch (error) {
      console.error("Error exporting data to Excel:", error);
      toast.error('Error exporting data to Excel');
      setLoading(false);
    }
  };

  const exportScaleDataToExcel = async () => {
    setLoading(true);
    let requestData = {
      filter: filter,
      startRow: null,
      endRow: null,
    };

    if (tableParams.column) {
      requestData.sortField = tableParams.field;
      requestData.sortDirection = tableParams.order === "ascend" ? 'asc' : 'desc';
    }

    try {
      const response = await api.get(`/${table}/getall`, { params: requestData });
      if (!response.data.status) {
        toast.error('Failed to get Data');
      } else {
        setLoading(false);
        const excelData = getExcelScale(response.data.data || [], response.data.summary); // Ensure data is array
        exportToExcel(excelData, title);
      }
    } catch (error) {
      console.error("Error exporting scale data to Excel:", error);
      toast.error('Error exporting scale data to Excel');
      setLoading(false);
    }
  };

  const exportToXML = async (data, title) => {
    const zip = new JSZip();

    // Ensure data.transformedData is an array
    (data.transformedData || []).forEach((item, index) => {
      const formattedDate = moment(item.date, 'YYYY-MM-DD').format('MM-DD-YYYY');

      let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
      xml += `<zed>\n`;
      xml += `  <no>${'LeVant'}</no>\n`;
      xml += `  <invoice_no>${item.id || ''}</invoice_no>\n`;
      xml += `  <zdate>${formattedDate}</zdate>\n`;
      xml += `  <amount>${numberFormatter(item.total)}</amount>\n`;
      xml += `</zed>\n`;

      zip.file(`Levant-${index + 1}.xml`, xml);
    });

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${title}.zip`);
    });
  };

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination.current, tableParams.field, tableParams.order, filter, refreshTable]);

  const reportModal = () => {
    setShowReportModal(!showReportModal);
  };

  const reportStatementModal = () => {
    setShowStatementReportModal(!showStatementReportModal);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const customPagination = {
    ...tableParams.pagination,
    itemRender: (current, type, originalElement) => {
      if (type === 'prev') {
        return <IoIosArrowBack size={20} style={{ marginRight: '12px' }} />;
      }
      if (type === 'next') {
        return <IoIosArrowForward size={20} style={{ marginLeft: '12px' }} />;
      }
      return originalElement;
    },
    showTotal: (total, range) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <div>
          {tableFooter}
        </div>
        <div>{`Total: ${total} items`}</div>
        <div style={{ position: 'absolute', left: '20px', transform: 'translateX(-50%)' }}>
          <DropdownButton
            drop={'up'}
            title={<BiExport size={25} color={'#2b7b76'} />}
            size={'sm'}
            variant="white"
            className="custom-dropdown-button">
            <Dropdown.Item eventKey="1" onClick={() => exportDataToExcel()}>Excel .xlsx</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item eventKey="2" onClick={() => reportModal()}>Summary .pdf</Dropdown.Item>

            {table === 'accountStatement' && (
              <Dropdown.Item eventKey="3" onClick={() => reportStatementModal()}>Report .pdf</Dropdown.Item>
            )}

            {table === 'product' && (
              <Dropdown.Item eventKey="4" onClick={() => exportScaleDataToExcel()}>Excel scale .xlsx</Dropdown.Item>
            )}

            {table === 'transaction' && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item eventKey="5" onClick={async () => {
                  setLoading(true);
                  let requestData = {
                    filter: filter,
                    startRow: null,
                    endRow: null,
                  };

                  if (tableParams.column) {
                    requestData.sortField = tableParams.field;
                    requestData.sortDirection = tableParams.order === "ascend" ? 'asc' : 'desc';
                  }

                  try {
                    const response = await api.get(`/${table}/getall`, { params: requestData });
                    if (!response.data.status) {
                      toast.error('Failed to get Data');
                    } else {
                      setLoading(false);
                      const xmlData = getExcelData(response.data.data || [], response.data.summary); // Ensure data is array
                      exportToXML(xmlData, title);
                    }
                  } catch (error) {
                    console.error("Error exporting XML:", error);
                    toast.error('Error exporting XML');
                    setLoading(false);
                  }
                }}>XML .zip</Dropdown.Item>
              </>
            )}
          </DropdownButton>
        </div>
      </div>
    ),
  };

  const rowSelection = selection ? {
    type: 'checkbox',
    onChange: (selectedRowKeys, selectedRows) => {
      if (setCheckedArray) {
        setCheckedArray(selectedRowKeys);
      }
    },
  } : null;

  const handleRowClick = (record) => {
    if (onRowClick) {
      onRowClick(record);
    }
  };

  const onDragEnd = ({ active, over }) => {
  if (active.id !== over?.id) {
    setDataSource((prevState) => {
      const currentData = prevState || [];
      const activeIndex = currentData.findIndex(
        (record) => record.id === active?.id,
      );
      const overIndex = currentData.findIndex(
        (record) => record.id === over?.id,
      );
      
      if (activeIndex === -1 || overIndex === -1) return prevState;
      
      const newDataSource = arrayMove(currentData, activeIndex, overIndex);
      
      // Call the parent's callback with the new order
      if (onDragEndProp) {
        onDragEndProp(newDataSource);
      }
      
      return newDataSource;
    });
  }
};

  const columnsWithDragHandle = useMemo(() => {
    if (isDraggable) {
      return [{ key: 'sort', align: 'center', width: 80, render: () => <DragHandle /> }, ...columnDefs];
    }
    return columnDefs;
  }, [isDraggable, columnDefs]);

  const components = useMemo(() => {
    if (isDraggable) {
      return {
        body: {
          row: DraggableRow,
        },
      };
    }
    return {};
  }, [isDraggable]);


  return (
    <div style={{ userSelect: 'none' }}>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              rowHoverBg: '#e6f4ff',
              headerSortActiveBg: '#00709d',
              headerSortHoverBg: '#00709d',
            },
          },
        }}
      >
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            // Ensure dataSource is an array before mapping
            items={(dataSource || []).map((i) => i.id)} // FIX: Added (dataSource || [])
            strategy={verticalListSortingStrategy}
          >
            <Table
              className={height ? 'ant_table_mini' : 'ant_table'}
              columns={columnsWithDragHandle}
              rowSelection={rowSelection}
              rowKey={(record) => record.id}
              dataSource={dataSource}
              loading={loading}
              onChange={handleTableChange}
              scroll={{ y: height ? '60vh' : '67vh' }}
              size="small"
              pagination={customPagination}
              rowClassName={(record, index) => {
                return index % 2 === 0 ? 'even-row' : 'odd-row';
              }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                'data-row-key': record.id,
              })}
              components={components}
            />
          </SortableContext>
        </DndContext>
      </ConfigProvider>

      <ReportModal
        showModal={showReportModal}
        toggleModal={reportModal}
        api={api}
        report={report}
        reportRef={reportRef}
      />
      {showStatementReportModal && (
        <ReportModal
          showModal={showStatementReportModal}
          toggleModal={reportStatementModal}
          api={api}
          report={statementreport}
          reportRef={statementreportRef}
        />
      )}
    </div>
  );
};

export default DragableTable;